import { useQuery } from "@tanstack/react-query"
import { apiFetch } from "./sdk"

/** Admin → System status (backend lib/system-status.ts). Types mirror the backend response. */

export type Tone = "ok" | "warn" | "down" | "unknown"
export type Problem = { tone: "warn" | "down"; area: string; title: string; detail?: string; link?: string }
export type ServiceRow = {
  name: string
  label: string
  running: number
  desired: number
  pending: number
  min: number | null
  max: number | null
  cpu: number | null
  memory: number | null
  cpu_series: (number | null)[]
  deploy: { state: string | null; started_at: string | null; completed_at?: string | null; revision: string | null; in_progress: boolean }
  tone: Tone
  /** No task running because a deploy is replacing them (a warning, not down). */
  restarting?: boolean
}
/** deploy_pause: late only because the background worker is being deployed (not a problem). */
export type JobRow = { name: string; label: string; schedule: string; critical: boolean; last_at: string | null; ms: number | null; ok: boolean | null; error: string | null; state: "ok" | "late" | "failed" | "unknown"; late_minutes: number; deploy_pause?: boolean }
/** Unhandled errors grouped by fingerprint (backend lib/observability/store.ts). */
export type ErrorGroup = {
  id: string
  fingerprint: string
  source: string
  kind: string
  message: string
  frames: string[]
  route: string | null
  method: string | null
  status: number | null
  release: string | null
  count_24h: number
  count_total: number
  first_seen_at: string
  last_seen_at: string
  last_request_id: string | null
  spiking: boolean
  alerted: boolean
  logs_url: string | null
  fingerprint_logs_url: string | null
  /** The last occurrence (browser errors): page path (token pages by route only), what caught it, browser, page cache state, React's component stack. */
  path?: string | null
  where?: string | null
  browser?: string | null
  page_cache?: string | null
  component_stack?: string | null
}
/** Browser noise and deploy skew, counted apart from errors (backend lib/observability/noise.ts). */
export type NoiseBucket = { reason: string; label: string; count_24h: number; kinds: number; last_seen_at: string }
export type RecentErrors = { total_24h: number; groups_24h: number; new_24h: number; spiking: number; groups: ErrorGroup[]; noise?: { total_24h: number; buckets: NoiseBucket[] } }
/** Checkout payments of the last hour (backend lib/payment-health.ts). */
export type PaymentHealth = {
  attempts: number
  approved: number
  declined: number
  customer: number
  system_failed: number
  checkouts: number
  checkouts_declined: number
  decline_rate: number | null
  declines_by_reason: { reason: string; count: number }[]
  system_by_reason: { reason: string; count: number }[]
}

export type SystemStatus = {
  generated_at: string
  env: string | null
  region: string
  aws: { connected: boolean; error: string | null }
  overall: Tone
  problems: Problem[]
  services: ServiceRow[]
  database: null | { id: string; status: string | null; instance_class: string | null; cpu: number | null; cpu_series: (number | null)[]; connections: number | null; free_storage_gb: number | null; allocated_gb: number | null; max_allocated_gb: number | null; free_memory_gb: number | null; read_latency_ms: number | null; write_latency_ms: number | null; tone: Tone }
  redis: null | { id: string; status: string | null; node_type: string | null; cpu: number | null; memory_pct: number | null; connections: number | null; tone: Tone }
  traffic: null | { requests_15m: number | null; errors_15m: number | null; error_rate_15m: number | null; requests_series: (number | null)[]; errors_series: (number | null)[]; payments_failed_1h: number | null; declines_1h: number | null; tone: Tone }
  sites: { id: string; label: string; url: string; status: number | null; ms: number | null; error: string | null; tone: Tone }[]
  alarms: { name: string; state: string; reason: string | null; since: string | null; description: string | null }[]
  jobs: JobRow[]
  store: {
    orders_1h: number | null
    orders_24h: number | null
    last_order_at: string | null
    issues: { open: number; critical: number; high: number; normal: number; latest: { id: string; title: string; severity: string; area: string; last_seen_at: string }[] } | null
    messages_24h: { email_sent: number; email_failed: number; sms_sent: number; sms_failed: number } | null
    shipstation: { failed: number; on_hold: number; pending_over_1h: number } | null
    renewals: { paid_today: number; failed_today: number; overdue: number; processing_stuck: number } | null
    recovery: { id: string; status: string; requested: number; sent: number; failed: number; skipped: number; started_at: string | null; updated_at: string | null } | null
    outboxes: { klaviyo_pending_old: number; conversions_pending_old: number } | null
    payments_1h?: PaymentHealth | null
  }
  recent_errors?: RecentErrors | null
  errors: Record<string, string>
}

export function useSystemStatus() {
  return useQuery({
    queryKey: ["system-status"],
    queryFn: () => apiFetch<SystemStatus>(`/admin/terify/system-status`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })
}

export const fetchFreshStatus = () => apiFetch<SystemStatus>(`/admin/terify/system-status?fresh=1`)
